import { useParams } from "react-router-dom";
import { LinkTabs, type LinkTab } from "@/components/ui/link-tabs";
import { Button } from "@/components/ui/button";
import { DialogDescription, DialogFooter, DialogHeader, DialogTitle, Dialog } from "@/components/ui/dialog";
import { PageHeader } from "@/components/ui/page-header";
import { GetPaidDialogContent, ModeratorCorrectionDetails, type ModeratorGetPaidTab } from "@/components/get-paid";
import { moderatorCopyFor } from "@/components/moderator/moderator-copy";
import { ContractStatus } from "@/lib/types/contracts";
import { ModeratorTestList } from "./ModeratorTestList";
import type { useGetPaid } from "../useGetPaid";
import type { TestSubmissionTrashItem } from "@/api/tests.api";
import type { ModeratorTestItem } from "@/components/get-paid";

/**
 * - "all": grading and the contract steps as tabs (the admin page).
 * - "correction": the moderator's grading page, tests to grade only.
 * - "contracts": a contract step inside the moderator's contracts page, no header of its own.
 */
export type ModeratorGetPaidMode = "all" | "correction" | "contracts";

type Ctx = ReturnType<typeof useGetPaid>;

interface ModeratorGetPaidProps {
  mode: ModeratorGetPaidMode;
  t: Ctx["t"];
  details: Ctx["details"];
  onBack: () => void;
  itemScores: Ctx["itemScores"];
  setScore: Ctx["setScore"];
  savingScores: boolean;
  averages: { ph: number; pp: number; tp: number };
  totalWeighted: number;
  canValidate: boolean;
  handleSaveScores: () => void;
  handleFinishCorrection: () => void;
  moderationLang: string;
  setModerationLang: (v: string) => void;
  moderatorNavTab: ModeratorGetPaidTab;
  visiblePendingTests: Ctx["visiblePendingTests"];
  visibleCorrectedTests: Ctx["visibleCorrectedTests"];
  loadingTests: boolean;
  loadPendingTests: () => void;
  loadCorrectedTests: (contractStatus?: ContractStatus) => void;
  loadValidatedTests: () => void;
  loadModeratorContracts: () => void;
  loadSubmissionDetails: Ctx["loadSubmissionDetails"];
  handleSendContractForTest: Ctx["handleSendContractForTest"];
  handleConfirmFinalValidate: () => void;
  finalValidationDialog: Ctx["finalValidationDialog"];
  setFinalValidationDialog: Ctx["setFinalValidationDialog"];
  finalValidatingTestId: string | null;
  sendingContractTestId: string | null;
  contractProgressByTest: Ctx["contractProgressByTest"];
  isSuperAdmin: boolean;
  trashItems: TestSubmissionTrashItem[];
  trashLoading: boolean;
  trashError: boolean;
  trashHasMore: boolean;
  trashLoadingMore: boolean;
  onLoadMoreTrash: () => void;
  onTrash: (test: ModeratorTestItem, reason: string) => Promise<boolean>;
  onRestore: (id: string) => Promise<void>;
}

const STATUS_OF_TAB: Record<ModeratorGetPaidTab, ContractStatus | undefined> = {
  pending: undefined,
  draft: ContractStatus.DRAFT,
  sent: ContractStatus.SENT,
  viewed: ContractStatus.VIEWED,
  signed: ContractStatus.SIGNED,
  final_validated: ContractStatus.FINAL_VALIDATED,
};

/**
 * The admin page keeps every step on one screen, as tabs. The contract steps
 * use the same words as the moderator's contracts page.
 */
function adminStepTabs(lang: string, t: Ctx["t"]): LinkTab[] {
  const href = (section: string) => `/${lang}/admin/test-moderation?tab=moderator&section=${section}`;
  const copy = moderatorCopyFor(lang);
  const labels: Record<ModeratorGetPaidTab, string> = {
    pending: t.getPaid.toCorrect,
    draft: copy.contractTabDraft,
    sent: copy.contractTabSent,
    viewed: copy.contractTabViewed,
    signed: copy.contractTabSigned,
    final_validated: copy.contractTabValidated,
  };
  return (Object.keys(labels) as ModeratorGetPaidTab[]).map((key) => ({
    key,
    label: labels[key],
    href: href(key),
    match: key === "pending" ? [`/${lang}/admin/test-moderation`] : undefined,
  }));
}

export const ModeratorGetPaid = (props: ModeratorGetPaidProps) => {
  const { lang = "fr" } = useParams<{ lang: string }>();
  const { mode, t, details, moderatorNavTab, finalValidationDialog, setFinalValidationDialog, finalValidatingTestId } = props;
  const copy = moderatorCopyFor(lang);
  const isPending = mode === "correction" || (mode === "all" && moderatorNavTab === "pending");

  const handleRefresh = () => {
    if (isPending) {
      props.loadPendingTests();
      return;
    }
    const contractStatus = STATUS_OF_TAB[moderatorNavTab];
    if (contractStatus === ContractStatus.FINAL_VALIDATED) props.loadValidatedTests();
    else if (contractStatus) props.loadCorrectedTests(contractStatus);
    props.loadModeratorContracts();
  };

  const finalTestId = String(finalValidationDialog.test?.id || "");

  return (
    <div className="mx-auto w-full max-w-7xl space-y-6">
      {mode === "correction" ? <PageHeader title={copy.testCorrection} description={copy.testCorrectionDescription} /> : null}
      {mode === "all" ? (
        <>
          {/* Title = the menu entry, tabs right under it. */}
          <PageHeader title={copy.qualificationTests} description={t.getPaid.testCorrectionDesc} className="border-b-0 pb-0" />
          <LinkTabs label={copy.qualificationTests} tabs={adminStepTabs(lang, t)} />
        </>
      ) : null}

      {details && isPending ? (
        <ModeratorCorrectionDetails
          details={{ ...details, id: details.testId }}
          t={t}
          itemScores={props.itemScores}
          setScore={props.setScore}
          savingScores={props.savingScores}
          averages={props.averages}
          totalWeighted={props.totalWeighted}
          canValidate={props.canValidate}
          onSaveScores={props.handleSaveScores}
          onFinishCorrection={props.handleFinishCorrection}
          onBack={props.onBack}
        />
      ) : null}

      <ModeratorTestList
        t={t}
        tests={isPending ? props.visiblePendingTests : props.visibleCorrectedTests}
        isPending={isPending}
        emptyLabel={isPending ? t.getPaid.noPendingTests : t.getPaid.noCorrectedTests}
        moderationLang={props.moderationLang}
        setModerationLang={props.setModerationLang}
        loadingTests={props.loadingTests}
        onRefresh={handleRefresh}
        contractProgressByTest={props.contractProgressByTest}
        onDetails={props.loadSubmissionDetails}
        onSendContract={props.handleSendContractForTest}
        onFinalValidate={(test) => setFinalValidationDialog({ open: true, test })}
        sendingContractTestId={props.sendingContractTestId}
        finalValidatingTestId={finalValidatingTestId}
        isSuperAdmin={props.isSuperAdmin}
        trashItems={props.trashItems}
        trashLoading={props.trashLoading}
        trashError={props.trashError}
        trashHasMore={props.trashHasMore}
        trashLoadingMore={props.trashLoadingMore}
        onLoadMoreTrash={props.onLoadMoreTrash}
        onTrash={props.onTrash}
        onRestore={props.onRestore}
      />

      <Dialog open={finalValidationDialog.open} onOpenChange={(open) => setFinalValidationDialog({ open, test: open ? finalValidationDialog.test : null })}>
        <GetPaidDialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t.getPaid.confirmFinalValidation}</DialogTitle>
            <DialogDescription>
              {finalValidationDialog.test
                ? t.getPaid.confirmFinalValidationDesc
                    .replace("{name}", finalValidationDialog.test.userName || "cet utilisateur")
                    .replace("{score}", String(finalValidationDialog.test.totalScore ?? "-"))
                : t.getPaid.confirmFinalValidationGeneric}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setFinalValidationDialog({ open: false, test: null })}>
              {t.getPaid.cancel}
            </Button>
            <Button type="button" onClick={props.handleConfirmFinalValidate} disabled={!finalValidationDialog.test || finalValidatingTestId === finalTestId}>
              {finalValidatingTestId === finalTestId ? t.getPaid.validating : t.getPaid.confirm}
            </Button>
          </DialogFooter>
        </GetPaidDialogContent>
      </Dialog>
    </div>
  );
};
